import { zodResolver } from '@hookform/resolvers/zod';
import { ForgotPasswordRequest } from '@horevia/contracts';
import { Button, FormField, Input, Notice } from '@horevia/ui';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { forgotPassword } from '@/features/auth/queries';
import { errorMessage } from '@/lib/errors';

export const Route = createFileRoute('/forgot-password')({ component: ForgotPasswordPage });

type FormValues = z.infer<typeof ForgotPasswordRequest>;

function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(ForgotPasswordRequest),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await forgotPassword(values);
      setSentTo(values.email);
    } catch (error) {
      setFormError(errorMessage(error, t));
    }
  });

  if (sentTo) {
    return (
      <AuthLayout title={t('auth:forgot.sentTitle')}>
        <div className="stack">
          <Notice tone="success">{t('auth:forgot.sentBody', { email: sentTo })}</Notice>
          <Button asChild variant="secondary" block>
            <Link to="/login">{t('auth:forgot.backToLogin')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth:forgot.title')} subtitle={t('auth:forgot.subtitle')}>
      <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
        {formError ? (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        ) : null}

        <FormField
          label={t('auth:login.email')}
          error={form.formState.errors.email ? t('common:validation.email') : undefined}
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <Input
              id={field.id}
              type="email"
              autoComplete="username"
              placeholder={t('auth:login.emailPlaceholder')}
              icon={<Mail className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              {...form.register('email')}
            />
          )}
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={form.formState.isSubmitting}
        >
          {t('auth:forgot.submit')}
        </Button>

        <Button asChild variant="ghost" size="sm">
          <Link to="/login">{t('auth:forgot.backToLogin')}</Link>
        </Button>
      </form>
    </AuthLayout>
  );
}
